import { useEffect, useState, useCallback } from 'react';
import { AppLayout, Spinner, ErrorBanner, useAuth } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type {
  TeamLeaderboardEntry,
  IndividualLeaderboardEntry,
} from '@scoring/shared';
import { useApiClient } from '../hooks/useApiClient.js';

/**
 * Shape of an event in the participant's event history.
 * Matches the `/account/events` response structure.
 */
interface EventHistoryEntry {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventStatus: string;
  totalScore: number | null;
  stationsCompleted: number | null;
  totalStations: number | null;
  finalRank: number | null;
  teamName: string | null;
}

type TabView = 'team' | 'individual' | 'history';

/**
 * Leaderboard page — displays event leaderboard standings (team and individual)
 * with the participant's own position highlighted, plus event history.
 *
 * Requirements:
 *   - 7.1: team leaderboard ranked by total score descending
 *   - 8.1: individual leaderboard ranked by total hits descending
 *   - 16.1: participant can view all events they participated in
 *   - 16.2: past events show total score, stations completed, and final rank
 */
export function LeaderboardPage(): JSX.Element {
  const api = useApiClient();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<TabView>('team');
  const [teamEntries, setTeamEntries] = useState<TeamLeaderboardEntry[]>([]);
  const [individualEntries, setIndividualEntries] = useState<IndividualLeaderboardEntry[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [eventHistory, setEventHistory] = useState<EventHistoryEntry[]>([]);
  const [currentEventId, setCurrentEventId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The participant's own participant ID in the current event (for highlighting)
  const [ownParticipantId, setOwnParticipantId] = useState<string | null>(null);
  const [ownTeamId, setOwnTeamId] = useState<string | null>(null);

  const fetchData = useCallback(
    async (client: ApiClient): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        // Fetch event history to determine the current event and show history
        const eventsData = await client.request<{ events: EventHistoryEntry[] }>('GET', '/account/events');
        const events = eventsData.events;

        setEventHistory(events);

        // Find the current active event for leaderboard display
        const activeEvent = events.find((e) => e.eventStatus === 'active');
        const publishedEvent = events.find((e) => e.eventStatus === 'published');
        const completeEvent = events.find((e) => e.eventStatus === 'complete');
        const event = activeEvent ?? publishedEvent ?? completeEvent ?? null;

        if (event) {
          setCurrentEventId(event.eventId);

          // Fetch leaderboard data for the current event
          const leaderboard = await client.request<{
            team: TeamLeaderboardEntry[];
            individual: IndividualLeaderboardEntry[];
            lastUpdated: string;
          }>('GET', `/events/${encodeURIComponent(event.eventId)}/leaderboard`);

          setTeamEntries(leaderboard.team);
          setIndividualEntries(leaderboard.individual);
          setLastUpdated(leaderboard.lastUpdated);

          // Try to determine the participant's own IDs for highlighting
          // Look for the participant in the individual leaderboard by matching user email/name
          if (user) {
            const ownEntry = leaderboard.individual.find(
              (entry) =>
                entry.participantName.toLowerCase() === user.email.toLowerCase() ||
                entry.participantName.toLowerCase() === user.username.toLowerCase(),
            );
            if (ownEntry) {
              setOwnParticipantId(ownEntry.participantId);
              setOwnTeamId(
                leaderboard.team.find((t) => t.teamName === ownEntry.teamName)?.teamId ?? null,
              );
            }
          }
        } else {
          setCurrentEventId(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load leaderboard data');
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!cancelled) {
        await fetchData(api);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, fetchData]);

  const tabButtonStyle = (isActive: boolean) => ({
    padding: '0.5rem 1rem',
    border: 'none',
    borderBottom: isActive ? '2px solid var(--color-brand-green, #1B4332)' : '2px solid transparent',
    background: 'none',
    color: isActive ? 'var(--color-brand-green, #1B4332)' : 'var(--color-text-secondary, #6B7280)',
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer' as const,
    fontSize: 'var(--font-size-body-sm, 0.875rem)',
  });

  const highlightStyle = {
    backgroundColor: '#e8f0fe',
    fontWeight: 600,
  };

  return (
    <AppLayout title="Leaderboard">
      {loading && <Spinner />}

      {error && !loading && <ErrorBanner>{error}</ErrorBanner>}

      {!loading && !error && (
        <>
          {/* Tab navigation */}
          <nav
            aria-label="Leaderboard views"
            style={{
              display: 'flex',
              gap: '0.25rem',
              borderBottom: '1px solid var(--color-border, #E5E7EB)',
              marginBottom: '1rem',
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab('team')}
              style={tabButtonStyle(activeTab === 'team')}
              aria-selected={activeTab === 'team'}
              role="tab"
            >
              Team
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('individual')}
              style={tabButtonStyle(activeTab === 'individual')}
              aria-selected={activeTab === 'individual'}
              role="tab"
            >
              Individual
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              style={tabButtonStyle(activeTab === 'history')}
              aria-selected={activeTab === 'history'}
              role="tab"
            >
              Event History
            </button>
          </nav>

          {/* Team Leaderboard Tab */}
          {activeTab === 'team' && (
            <section aria-label="Team leaderboard">
              {!currentEventId ? (
                <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No active event to display leaderboard for.</p>
              ) : (
                <>
                  {lastUpdated && (
                    <p style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)', marginBottom: '0.75rem' }}>
                      Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                    </p>
                  )}
                  {teamEntries.length === 0 ? (
                    <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No team scores recorded yet.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        aria-label="Team leaderboard rankings"
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: '2px solid var(--color-border, #E5E7EB)',
                              textAlign: 'left',
                            }}
                          >
                            <th scope="col" style={{ padding: '0.5rem' }}>
                              #
                            </th>
                            <th scope="col" style={{ padding: '0.5rem' }}>
                              Team
                            </th>
                            <th scope="col" style={{ padding: '0.5rem', textAlign: 'right' }}>
                              Score
                            </th>
                            <th scope="col" style={{ padding: '0.5rem', textAlign: 'right' }}>
                              Members
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamEntries.map((entry) => {
                            const isOwn = ownTeamId !== null && entry.teamId === ownTeamId;
                            return (
                              <tr
                                key={entry.teamId}
                                style={{
                                  borderBottom: '1px solid #eee',
                                  ...(isOwn ? highlightStyle : {}),
                                }}
                                aria-current={isOwn ? 'true' : undefined}
                              >
                                <td style={{ padding: '0.5rem' }}>{entry.rank}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  {entry.teamName}
                                  {isOwn && (
                                    <span
                                      style={{
                                        marginLeft: '0.5rem',
                                        fontSize: 'var(--font-size-body-sm, 0.875rem)',
                                        color: 'var(--color-brand-green, #1B4332)',
                                      }}
                                    >
                                      (Your team)
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                  {entry.totalScore}
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                  {entry.participantCount}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* Individual Leaderboard Tab */}
          {activeTab === 'individual' && (
            <section aria-label="Individual leaderboard">
              {!currentEventId ? (
                <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No active event to display leaderboard for.</p>
              ) : (
                <>
                  {lastUpdated && (
                    <p style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)', marginBottom: '0.75rem' }}>
                      Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                    </p>
                  )}
                  {individualEntries.length === 0 ? (
                    <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No individual scores recorded yet.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        aria-label="Individual leaderboard rankings"
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: '2px solid var(--color-border, #E5E7EB)',
                              textAlign: 'left',
                            }}
                          >
                            <th scope="col" style={{ padding: '0.5rem' }}>
                              #
                            </th>
                            <th scope="col" style={{ padding: '0.5rem' }}>
                              Name
                            </th>
                            <th scope="col" style={{ padding: '0.5rem' }}>
                              Team
                            </th>
                            <th scope="col" style={{ padding: '0.5rem', textAlign: 'right' }}>
                              Score
                            </th>
                            <th scope="col" style={{ padding: '0.5rem', textAlign: 'right' }}>
                              Stations
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {individualEntries.map((entry) => {
                            const isOwn =
                              ownParticipantId !== null &&
                              entry.participantId === ownParticipantId;
                            return (
                              <tr
                                key={entry.participantId}
                                style={{
                                  borderBottom: '1px solid #eee',
                                  ...(isOwn ? highlightStyle : {}),
                                }}
                                aria-current={isOwn ? 'true' : undefined}
                              >
                                <td style={{ padding: '0.5rem' }}>{entry.rank}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  {entry.participantName}
                                  {isOwn && (
                                    <span
                                      style={{
                                        marginLeft: '0.5rem',
                                        fontSize: 'var(--font-size-body-sm, 0.875rem)',
                                        color: 'var(--color-brand-green, #1B4332)',
                                      }}
                                    >
                                      (You)
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem' }}>{entry.teamName ?? '—'}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                  {entry.totalScore}
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                  {entry.stationsCompleted}/{entry.totalStations}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* Event History Tab */}
          {activeTab === 'history' && (
            <section aria-label="Event history">
              {eventHistory.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No past events found.</p>
              ) : (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'grid',
                    gap: '0.75rem',
                  }}
                >
                  {eventHistory.map((event) => (
                    <li
                      key={event.eventId}
                      style={{
                        border: '1px solid var(--color-border, #E5E7EB)',
                        borderRadius: 'var(--radius-card, 12px)',
                        padding: '0.75rem 1rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.25rem',
                        }}
                      >
                        <strong style={{ fontSize: 'var(--font-size-body-md, 1rem)' }}>{event.eventName}</strong>
                        <span
                          style={{
                            fontSize: 'var(--font-size-body-sm, 0.875rem)',
                            padding: '0.125rem 0.5rem',
                            borderRadius: 'var(--radius-button, 8px)',
                            backgroundColor:
                              event.eventStatus === 'active'
                                ? '#e6f4ea'
                                : event.eventStatus === 'complete' ||
                                    event.eventStatus === 'closed'
                                  ? '#f0f0f0'
                                  : '#fff8e1',
                            color:
                              event.eventStatus === 'active'
                                ? 'var(--color-brand-green, #1B4332)'
                                : event.eventStatus === 'complete' ||
                                    event.eventStatus === 'closed'
                                  ? 'var(--color-text-secondary, #6B7280)'
                                  : '#f57c00',
                          }}
                        >
                          {event.eventStatus}
                        </span>
                      </div>
                      <p style={{ margin: '0.25rem 0', fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>
                        {event.eventDate}
                        {event.teamName && ` · ${event.teamName}`}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          gap: '1.5rem',
                          marginTop: '0.5rem',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                        }}
                      >
                        {event.totalScore !== null && (
                          <div>
                            <span style={{ color: 'var(--color-text-secondary, #6B7280)' }}>Score: </span>
                            <strong>{event.totalScore}</strong>
                          </div>
                        )}
                        {event.stationsCompleted !== null && event.totalStations !== null && (
                          <div>
                            <span style={{ color: 'var(--color-text-secondary, #6B7280)' }}>Stations: </span>
                            <strong>
                              {event.stationsCompleted}/{event.totalStations}
                            </strong>
                          </div>
                        )}
                        {event.finalRank !== null && (
                          <div>
                            <span style={{ color: 'var(--color-text-secondary, #6B7280)' }}>Rank: </span>
                            <strong>#{event.finalRank}</strong>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </AppLayout>
  );
}
